import React, { useMemo, useRef } from 'react'
import classnames from 'classnames/bind'
import { List, AutoSizer, CellMeasurerCache, CellMeasurer, WindowScroller } from 'react-virtualized'
import { useRouter } from 'next/router'
import InfiniteScroll from 'react-infinite-scroller'
import { isNull } from 'lodash-es'

// Libs
import { useLiveData } from '@/hooks/useLiveData'
import { useVirtualizedScrollRestoration } from '@/hooks/useVirtualizedScroll'

// Components
import ProductCard from '@/components/shared/ProductCard'
import RowItemMeasurer from './components/RowItemMeasurer'

// Constants
import { ProductInfoType } from '@/api/fetchProductData'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

type VirtualizedMallListProps = {
  className?: string
  categoryListData: any
  gtmItemEventParams: {
    itemListName: string
    itemListId?: string
  }
  observer?: IntersectionObserver
}

const rowDataLength = 2
const cache = new CellMeasurerCache({
  fixedWidth: true,
})

const VirtualizedMallList = (props: VirtualizedMallListProps) => {
  const { className, categoryListData, gtmItemEventParams, observer } = props
  const { liveData } = useLiveData({})
  const insightInfo = { product: liveData?.productMap }
  const listRef = useRef<any>(null)
  const router = useRouter()

  const { scrollToIndex, setViewedRowIndex } = useVirtualizedScrollRestoration(router, { listRef })

  const dataList = useMemo(() => {
    if (categoryListData.isSuccess) {
      // 兩個兩個為一個 row
      const flatData = categoryListData.data.pages
        .map((item: { productsInfo: ProductInfoType[] }) => item.productsInfo)
        .flat()
        .filter((rowItem: { productsInfo: ProductInfoType[] }) => !isNull(rowItem))
      const rowList = flatData.reduce((acc: ProductInfoType[][], cur: ProductInfoType, index: number) => {
        if (index % rowDataLength === 0) {
          acc.push([cur])
        } else {
          acc[acc.length - 1].push(cur)
        }
        return acc
      }, [])
      return rowList
    } else {
      return []
    }
  }, [categoryListData.data, categoryListData.isFetchingNextPage, categoryListData.isSuccess])

  const dataTotalCount = categoryListData?.data?.pages[0]?.totalPage?.totalCount || 0
  const listRowCount = dataList.flat().length < dataTotalCount ? dataList?.length : dataList.length

  const handleNextpage = async () => {
    if (!categoryListData.isFetchingNextPage) {
      await categoryListData.fetchNextPage()
      return
    }
  }
  const handleCardClick = (rowIndex: number) => {
    setViewedRowIndex(rowIndex)
  }

  const rowRenderer = ({ index, key, style, parent }: any) => {
    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
        {({ measure }) => (
          <React.Fragment key={key}>
            {!!dataList[index] && (
              <RowItemMeasurer measure={measure}>
                <div style={style} className={cx('mallList__list')}>
                  {dataList[index].map((item: ProductInfoType) => (
                    <ProductCard
                      key={`row-${index}-product-${item.productId}`}
                      {...item}
                      {...insightInfo}
                      observer={observer}
                      gtmItemEventParams={gtmItemEventParams}
                      handleClick={() => handleCardClick(index)}
                    />
                  ))}
                </div>
              </RowItemMeasurer>
            )}
          </React.Fragment>
        )}
      </CellMeasurer>
    )
  }

  const onResize = () => {
    cache.clearAll()
    listRef.current.recomputeRowHeights()
  }

  return (
    <div className={cx('mallList', className)}>
      <InfiniteScroll loadMore={handleNextpage} hasMore={categoryListData.hasNextPage}>
        <AutoSizer onResize={onResize} disableHeight>
          {({ width }) => (
            <>
              <WindowScroller>
                {({ height, isScrolling, scrollTop, onChildScroll, registerChild }) => (
                  <div
                    ref={(ref: any) => {
                      registerChild(ref)
                    }}
                  >
                    <List
                      ref={listRef}
                      deferredMeasurementCache={cache}
                      rowRenderer={rowRenderer}
                      rowCount={listRowCount}
                      onScroll={onChildScroll}
                      rowHeight={cache.rowHeight}
                      width={width}
                      autoHeight
                      height={height}
                      overscanRowCount={0}
                      // https://github.com/bvaughn/react-virtualized/issues/1675
                      scrollTop={isScrolling && (scrollTop as any)}
                      isScrolling={isScrolling}
                      scrollToIndex={scrollToIndex}
                    />
                  </div>
                )}
              </WindowScroller>
            </>
          )}
        </AutoSizer>
      </InfiniteScroll>
    </div>
  )
}

export default VirtualizedMallList
